import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styled from 'styled-components'

import { cardFrameCSS } from '@/@bothrs-ui/web/components/card/Card'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'

import type { ComponentPropsWithoutRef } from 'react'

/* ------------------------------------------------------------------------------------------------- *
 * AppointmentNumberCard
 * ------------------------------------------------------------------------------------------------- */

// The whole card is the button; what pressing it does (copying) is the call site's `onClick`.
// No aria-label: the accessible name comes from the card content, so the number itself is announced.
type AppointmentNumberRootProps = ComponentPropsWithoutRef<'button'>

const AppointmentNumberRoot = ({ children, ...props }: AppointmentNumberRootProps) => (
  <StCard type='button' {...props}>
    {children}
  </StCard>
)

/* ------------------------------------------------------------------------------------------------- *
 * AppointmentNumberCard.Label
 * ------------------------------------------------------------------------------------------------- */

type AppointmentNumberLabelProps = Omit<ComponentPropsWithoutRef<typeof Span>, 'type'>

const AppointmentNumberLabel = ({ children, ...props }: AppointmentNumberLabelProps) => (
  <StSecondaryText type='BodyMediumRegular' {...props}>
    {children}
  </StSecondaryText>
)

/* ------------------------------------------------------------------------------------------------- *
 * AppointmentNumberCard.Value
 * ------------------------------------------------------------------------------------------------- */

type AppointmentNumberValueProps = ComponentPropsWithoutRef<'span'>

const AppointmentNumberValue = ({ children, ...props }: AppointmentNumberValueProps) => (
  <StNumber {...props}>{children}</StNumber>
)

/* ------------------------------------------------------------------------------------------------- *
 * AppointmentNumberCard.Icon
 * ------------------------------------------------------------------------------------------------- */

// The icon and its title come from the call site, which knows what pressing the card does.
type AppointmentNumberIconProps = ComponentPropsWithoutRef<typeof FontAwesomeIcon>

const AppointmentNumberIcon = (props: AppointmentNumberIconProps) => <StCopyIcon fontSize={20} {...props} />

/* ------------------------------------------------------------------------------------------------- *
 * AppointmentNumberCard.Hint
 * ------------------------------------------------------------------------------------------------- */

type AppointmentNumberHintProps = Omit<ComponentPropsWithoutRef<typeof Span>, 'type'>

const AppointmentNumberHint = ({ children, ...props }: AppointmentNumberHintProps) => (
  <StHint type='BodyMediumRegular' {...props}>
    {children}
  </StHint>
)

/* ------------------------------------------------------------------------------------------------- *
 * Compound export
 * ------------------------------------------------------------------------------------------------- */

const AppointmentNumberCard = Object.assign(AppointmentNumberRoot, {
  Label: AppointmentNumberLabel,
  Value: AppointmentNumberValue,
  Icon: AppointmentNumberIcon,
  Hint: AppointmentNumberHint,
})

export { AppointmentNumberCard }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

// The card frame on a button, with the design's tinted fill. Next to a taller location card the
// content sits at the bottom, level with the address. The button's own look is reset, and a
// keyboard focus draws a ring in place of the outline.
const StCard = styled.button`
  margin: 0;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  ${cardFrameCSS()}
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space1};
  width: 100%;
  background-color: ${({ theme }) => theme.Colors.Neutrals.Lighter};

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.Components.Button.Primary.Fill};
  }
`

const StSecondaryText = styled(Span)`
  color: ${({ theme, color }) => color ?? theme.Colors.Neutrals.Darker};
`

const StNumber = styled.span`
  ${({ theme }) => theme.Typography.Heading2}
  color: ${({ theme }) => theme.Components.Button.Tertiary.Text};
`

const StCopyIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.Colors.Neutrals.Dark};
`

// The hint stands further from the number than the label does, as the design has it.
const StHint = styled(StSecondaryText)`
  margin-top: ${({ theme }) => theme.UI.SpacingPx.Space2};
`
